import {Box, MenuItem, Stack, TextField, Typography} from "@mui/material";
import type {TimeMark, TimeMarkPrecision} from "../../../../../sdo/TimeMark.ts";
import {formatTimeMark, PRECISION_CODE, PRECISION_OPTIONS} from "../../../../../sdo/TimeMark.ts";
import {fieldSx, selectSx, SummaryPill, SummaryPillRow} from "../resolvers/editors/ResolverUtils.tsx";

function hasText(s: string | undefined | null): boolean {
    return !!(s && s.trim().length > 0);
}

interface TimeMarkEditorProps {
    draft: TimeMark;
    editing: boolean;
    onChange: (updated: TimeMark) => void;
}

const multilineFieldSx = {
    ...fieldSx,
    "& .MuiOutlinedInput-root": {
        ...(fieldSx as Record<string, unknown>)["& .MuiOutlinedInput-root"] as object,
        height: "auto",
        padding: "10px 12px",
    },
    "& .MuiOutlinedInput-input": {
        color: "#f2f5fa",
        fontSize: "0.80rem",
        lineHeight: 1.6,
    },
};

const labelSx = {
    color: "#a8b8c0",
    fontSize: "0.70rem",
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: "uppercase" as const,
    minWidth: "100px",
};

// --- Date <-> input string converters --------------------------------------
// Web inputs (type=date / datetime-local) work with ISO-style strings;
// the SDO stores epoch milliseconds. These helpers bridge the two.

function pad(n: number): string {
    return String(n).padStart(2, "0");
}

function toDateInput(epochMs: number): string {
    if (!epochMs || epochMs <= 0) return "";
    const d = new Date(epochMs);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function toDateTimeInput(epochMs: number, withSeconds: boolean): string {
    if (!epochMs || epochMs <= 0) return "";
    const d = new Date(epochMs);
    const base = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    return withSeconds ? `${base}:${pad(d.getSeconds())}` : base;
}

function fromDateInput(value: string, fallback: number): number {
    if (!value) return fallback;
    // value is "YYYY-MM-DD"
    const [y, m, d] = value.split("-").map((s) => parseInt(s, 10));
    if (!y || !m || !d) return fallback;
    return new Date(y, m - 1, d, 0, 0, 0).getTime();
}

function fromDateTimeInput(value: string, fallback: number): number {
    if (!value) return fallback;
    // value is "YYYY-MM-DDTHH:MM" or "YYYY-MM-DDTHH:MM:SS"
    const dt = new Date(value);
    return isNaN(dt.getTime()) ? fallback : dt.getTime();
}

function getYear(epochMs: number): number {
    return epochMs > 0 ? new Date(epochMs).getFullYear() : new Date().getFullYear();
}

function getMonth(epochMs: number): number {
    return epochMs > 0 ? new Date(epochMs).getMonth() + 1 : 1;
}

function buildEpochFromYear(year: number): number {
    return new Date(year, 0, 1, 0, 0, 0).getTime();
}

function buildEpochFromYearMonth(year: number, month: number): number {
    return new Date(year, month - 1, 1, 0, 0, 0).getTime();
}

// ---------------------------------------------------------------------------

function TimeMarkEditor({draft, editing, onChange}: TimeMarkEditorProps) {
    const upd = <K extends keyof TimeMark>(key: K, value: TimeMark[K]) => {
        onChange({...draft, [key]: value});
    };

    if (!editing) {
        // Read-only summary view — uses the same LeftPillRow + SummaryPill pattern
        // as MediaEditor and WebReferenceEditor for visual consistency across cards.
        const formatted = formatTimeMark(draft);
        return (
            <Stack direction="column" spacing={0.5} sx={{width: "100%", maxWidth: 760, mx: "auto", alignItems: "stretch"}}>
                <SummaryPillRow>
                    <SummaryPill label="Precision" value={draft.precision || "—"}/>
                    <SummaryPill label="Time" value={formatted || "—"}/>
                </SummaryPillRow>
                {hasText(draft.title) && (
                    <SummaryPillRow>
                        <SummaryPill label="Title" value={draft.title}/>
                    </SummaryPillRow>
                )}
                {hasText(draft.details) && (
                    <SummaryPillRow>
                        <SummaryPill label="Details" value={draft.details}/>
                    </SummaryPillRow>
                )}
            </Stack>
        );
    }

    const code = PRECISION_CODE[draft.precision];
    const showYear = code <= 2;
    const showMonth = code === 2;
    const showDate = code >= 3;
    const showHour = code >= 4;
    const showMinute = code >= 5;
    const showSecond = code >= 6;

    // Decide which datetime input shape to render for the day-and-below precisions
    let dateInput: React.ReactNode = null;
    if (showDate) {
        if (showSecond) {
            // datetime-local with seconds (use step=1)
            dateInput = (
                <TextField
                    type="datetime-local"
                    label="Date & Time"
                    value={toDateTimeInput(draft.time, true)}
                    onChange={(e) => upd("time", fromDateTimeInput(e.target.value, draft.time))}
                    size="small"
                    slotProps={{
                        inputLabel: {shrink: true},
                        htmlInput: {step: 1},
                    }}
                    sx={{...fieldSx, flex: "1 1 auto", minWidth: 240}}
                />
            );
        } else if (showMinute) {
            dateInput = (
                <TextField
                    type="datetime-local"
                    label="Date & Time"
                    value={toDateTimeInput(draft.time, false)}
                    onChange={(e) => upd("time", fromDateTimeInput(e.target.value, draft.time))}
                    size="small"
                    slotProps={{inputLabel: {shrink: true}}}
                    sx={{...fieldSx, flex: "1 1 auto", minWidth: 220}}
                />
            );
        } else if (showHour) {
            // Date + hour-only spinner (no native HTML input for hour-only; use a pair)
            dateInput = (
                <>
                    <TextField
                        type="date"
                        label="Date"
                        value={toDateInput(draft.time)}
                        onChange={(e) => {
                            const ms = fromDateInput(e.target.value, draft.time);
                            // preserve current hour
                            const cur = new Date(ms || Date.now());
                            const hour = draft.time > 0 ? new Date(draft.time).getHours() : 0;
                            cur.setHours(hour, 0, 0, 0);
                            upd("time", cur.getTime());
                        }}
                        size="small"
                        slotProps={{inputLabel: {shrink: true}}}
                        sx={{...fieldSx, flex: "1 1 auto", minWidth: 160}}
                    />
                    <TextField
                        type="number"
                        label="Hour (0-23)"
                        value={draft.time > 0 ? new Date(draft.time).getHours() : 0}
                        onChange={(e) => {
                            const h = Math.max(0, Math.min(23, parseInt(e.target.value, 10) || 0));
                            const d = new Date(draft.time > 0 ? draft.time : Date.now());
                            d.setHours(h, 0, 0, 0);
                            upd("time", d.getTime());
                        }}
                        size="small"
                        sx={{...fieldSx, width: 110}}
                    />
                </>
            );
        } else {
            // Day only
            dateInput = (
                <TextField
                    type="date"
                    label="Date"
                    value={toDateInput(draft.time)}
                    onChange={(e) => upd("time", fromDateInput(e.target.value, draft.time))}
                    size="small"
                    slotProps={{inputLabel: {shrink: true}}}
                    sx={{...fieldSx, flex: "1 1 auto", minWidth: 200}}
                />
            );
        }
    }

    return (
        <Stack direction="column" spacing={1.5} sx={{
            width: "100%",
            maxWidth: 760,
            mx: "auto",
            alignItems: "stretch",
        }}>
            {/* Precision + date/time row */}
            <Box sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1,
            }}>
                <Typography sx={labelSx}>Precision</Typography>
                <TextField
                    select
                    value={draft.precision}
                    onChange={(e) => {
                        const newPrecision = e.target.value as TimeMarkPrecision;
                        // If switching to a lower-precision mode, also normalize the time
                        // so the saved value matches what's displayed.
                        let newTime = draft.time > 0 ? draft.time : Date.now();
                        if (newPrecision === "YR") {
                            newTime = buildEpochFromYear(getYear(newTime));
                        } else if (newPrecision === "YR_MON") {
                            newTime = buildEpochFromYearMonth(getYear(newTime), getMonth(newTime));
                        }
                        onChange({...draft, precision: newPrecision, time: newTime});
                    }}
                    size="small"
                    sx={{...selectSx, minWidth: 160}}
                >
                    {PRECISION_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </TextField>
            </Box>

            {/* Date / time inputs (precision-dependent) */}
            <Box sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1,
            }}>
                {showYear && (
                    <TextField
                        type="number"
                        label="Year"
                        value={getYear(draft.time)}
                        onChange={(e) => {
                            const y = parseInt(e.target.value, 10);
                            if (!isNaN(y)) {
                                if (showMonth) {
                                    upd("time", buildEpochFromYearMonth(y, getMonth(draft.time)));
                                } else {
                                    upd("time", buildEpochFromYear(y));
                                }
                            }
                        }}
                        size="small"
                        sx={{...fieldSx, width: 120}}
                    />
                )}
                {showMonth && (
                    <TextField
                        type="number"
                        label="Month (1-12)"
                        value={getMonth(draft.time)}
                        onChange={(e) => {
                            const m = Math.max(1, Math.min(12, parseInt(e.target.value, 10) || 1));
                            upd("time", buildEpochFromYearMonth(getYear(draft.time), m));
                        }}
                        size="small"
                        sx={{...fieldSx, width: 130}}
                    />
                )}
                {dateInput}
            </Box>

            {/* Title */}
            <TextField
                value={draft.title || ""}
                onChange={(e) => upd("title", e.target.value)}
                label="Title"
                size="small"
                fullWidth
                sx={fieldSx}
            />

            {/* Details — multiline */}
            <TextField
                value={draft.details || ""}
                onChange={(e) => upd("details", e.target.value)}
                label="Details"
                size="small"
                fullWidth
                multiline
                rows={4}
                sx={multilineFieldSx}
            />
        </Stack>
    );
}

export default TimeMarkEditor;
